import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, Button, Typography, Switch } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import './modal.scss';
import { createCommand, updateCommand } from 'src/store/command';
import { userSelector } from 'src/store/auth';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const USER_PROPERTIES = [
  {
    value: 'watchTime',
    label: 'User watch time (hours)'
  },
  {
    value: 'watchStreak',
    label: 'User Watch Streak'
  }
  // {
  //   value: 'subscriptionStatus',
  //   label: 'Subscription status',
  //   options: [
  //     {
  //       value: 'isFollower',
  //       label: 'Follower'
  //     }
  //   ]
  // },
  // {
  //   value: 'role',
  //   label: 'Role (viewer/sub)'
  // }
];

const COMPONENT_BY_PROPERTIES = {
  watchTime: Input,
  watchStreak: Input,
  subscriptionStatus: Select
};

const OPERATORS = [
  { value: '>=', label: 'operators.greater_or_equal' }
  // extend operators (==, !=, etc.) when backend supports them
];

const PLAN_TICKET_LIMITS = {
  free: 50,
  pro_light: 100,
  pro: Infinity // бесконечность = нет лимита
};

export const CommandsModal = ({ form, selectedCommand, isModalOpen, onCancel }) => {
  const { t } = useTranslation('commands');

  const dispatch = useDispatch();

  const isEditMode = !!selectedCommand;
  const commandType = Form.useWatch('type', form) || 'default';

  const user = useSelector(userSelector);

  const subscriptionPlan = user?.subscription;

  const handleSubmit = useCallback(
    (values) => {
      const payload = {
        ...values,
        conditions: Array.isArray(values?.conditions) ? values.conditions : []
      };

      if (!isEditMode) {
        dispatch(createCommand({ payload, onCancel }));
      }

      if (isEditMode) {
        dispatch(updateCommand({ id: selectedCommand?._id, payload, onCancel }));
      }
    },
    [dispatch, isEditMode, selectedCommand, onCancel]
  );

  const currentPlanLimit = PLAN_TICKET_LIMITS[subscriptionPlan] ?? PLAN_TICKET_LIMITS.free;

  return (
    <Modal
      title={isEditMode ? t('modal.edit_title') : t('modal.create_title')}
      open={isModalOpen}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="type"
          label={t('fields.type')}
          rules={[{ required: true, message: t('errors.type_required') }]}
        >
          <Select>
            <Option value="default">{t('types.default')}</Option>
            <Option value="custom">{t('types.custom')}</Option>
          </Select>
        </Form.Item>

        {commandType === 'default' && (
          <>
            <Form.Item
              name="trigger"
              label={t('fields.trigger')}
              rules={[{ required: true, message: t('errors.trigger_required') }]}
            >
              <Input placeholder={t('placeholders.default_command_trigger')} />
            </Form.Item>

            <Form.Item
              name="message"
              label={t('fields.message')}
              rules={[{ required: true, message: t('errors.message_required') }]}
            >
              <TextArea rows={4} placeholder={t('placeholders.default_message')} />
            </Form.Item>

            <Paragraph type="secondary">{t('help.variables')}</Paragraph>
            <Paragraph type="secondary">{t('help.followage_variables')}</Paragraph>
          </>
        )}

        {commandType === 'custom' && (
          <>
            {/* Trigger name */}
            <Form.Item
              name="trigger"
              label={t('fields.trigger')}
              rules={[{ required: true, message: t('errors.trigger_required') }]}
            >
              <Input placeholder={t('placeholders.custom_command_trigger')} />
            </Form.Item>

            <Form.Item
              name="isGiveawayCommand"
              label={t('fields.is_giveaway_ticket_label')}
              valuePropName="checked"
              tooltip={t('fields.is_giveaway_ticket_help')}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.isGiveawayCommand !== cur.isGiveawayCommand}
            >
              {({ getFieldValue }) => {
                const isTicketCmd = getFieldValue('isGiveawayCommand');
                if (!isTicketCmd) return null;

                return (
                  <Form.Item
                    name="isFollower"
                    label={t('fields.is_follower_label')}
                    valuePropName="checked"
                    tooltip={t('fields.is_follower_help')}
                  >
                    <Switch disabled />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.isGiveawayCommand !== cur.isGiveawayCommand}
            >
              {({ getFieldValue }) => {
                const isTicketCmd = getFieldValue('isGiveawayCommand');

                if (!isTicketCmd) return null;

                return (
                  <Form.Item
                    name="trackLurkers"
                    label={t('fields.track_lurkers_label')}
                    valuePropName="checked"
                    tooltip={t('fields.track_lurkers_help')}
                  >
                    <Switch disabled />
                    {/* disabled={subscriptionPlan === 'free'} */}
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.isGiveawayCommand !== cur.isGiveawayCommand}
            >
              {({ getFieldValue }) => {
                const isTicketCmd = getFieldValue('isGiveawayCommand');

                if (!isTicketCmd) return null;

                const isUnlimited = currentPlanLimit === Infinity;

                return (
                  <Form.Item
                    name="maxTickets"
                    label={t('fields.max_tickets_label')}
                    rules={
                      isUnlimited
                        ? [
                            {
                              required: true,
                              message: t('errors.max_tickets_required')
                            }
                          ]
                        : [
                            {
                              required: true,
                              message: t('errors.max_tickets_required')
                            },
                            () => ({
                              validator(_, value) {
                                if (!value && value !== 0) return Promise.resolve();
                                if (value <= 0) {
                                  return Promise.reject(
                                    new Error(t('errors.max_tickets_positive'))
                                  );
                                }
                                if (value > currentPlanLimit) {
                                  return Promise.reject(
                                    new Error(
                                      t('errors.max_tickets_plan_limit', {
                                        limit: currentPlanLimit
                                      })
                                    )
                                  );
                                }
                                return Promise.resolve();
                              }
                            })
                          ]
                    }
                    extra={
                      isUnlimited
                        ? t('fields.max_tickets_help_unlimited')
                        : t('fields.max_tickets_help_limited', { limit: currentPlanLimit })
                    }
                  >
                    <Input
                      min={1}
                      max={isUnlimited ? undefined : currentPlanLimit}
                      type="number"
                      style={{ width: '100%' }}
                      placeholder={t('fields.max_tickets_placeholder')}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('sections.if_block')}
            </Text>

            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('help.conditions_optional')}
            </Paragraph>

            <Form.List name="conditions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => {
                    const { key, ...restField } = field;
                    const leftFieldPath = ['conditions', field.name, 'left'];

                    return (
                      <div key={key} className="commands-modal__conditions">
                        <Form.Item
                          {...restField}
                          name={[field.name, 'left']}
                          rules={[{ required: true, message: t('errors.left_required') }]}
                        >
                          <Select placeholder={t('fields.left_placeholder')}>
                            {USER_PROPERTIES.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[field.name, 'operator']}
                          rules={[{ required: true, message: t('errors.operator_required') }]}
                        >
                          <Select placeholder={t('fields.operator_placeholder')}>
                            {OPERATORS.map((op) => (
                              <Option key={op.value} value={op.value}>
                                {t(op.label)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        {/* right */}
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, cur) =>
                            prev?.conditions?.[field.name]?.left !==
                            cur?.conditions?.[field.name]?.left
                          }
                        >
                          {() => {
                            const leftValue = form.getFieldValue(leftFieldPath);
                            const RightComponent = COMPONENT_BY_PROPERTIES[leftValue] || Input;

                            // --- NUMERIC PROPERTIES ---
                            const isNumericProperty =
                              leftValue === 'watchTime' || leftValue === 'watchStreak';

                            const planLimits = {
                              free: { watchTime: 20, watchStreak: 3 },
                              pro_light: { watchTime: 50, watchStreak: 10 },
                              pro: { watchTime: Infinity, watchStreak: Infinity }
                            };

                            const { watchTime, watchStreak } = planLimits[subscriptionPlan];

                            const maxLimit =
                              leftValue === 'watchTime'
                                ? watchTime
                                : leftValue === 'watchStreak'
                                  ? watchStreak
                                  : Infinity;

                            return (
                              <Form.Item
                                {...restField}
                                name={[field.name, 'right']}
                                rules={[
                                  { required: true, message: t('errors.right_required') },
                                  ...(isNumericProperty
                                    ? [
                                        {
                                          validator: (_, value) => {
                                            if (
                                              value === undefined ||
                                              value === null ||
                                              value === ''
                                            ) {
                                              return Promise.resolve();
                                            }

                                            const num = Number(value);

                                            if (Number.isNaN(num)) {
                                              return Promise.reject(
                                                new Error(t('errors.right_must_be_number'))
                                              );
                                            }

                                            if (num < 0) {
                                              return Promise.reject(
                                                new Error(t('errors.right_cannot_be_negative'))
                                              );
                                            }

                                            if (num > maxLimit) {
                                              return Promise.reject(
                                                new Error(
                                                  t('errors.right_exceeds_plan_limit', {
                                                    limit: maxLimit === Infinity ? '∞' : maxLimit
                                                  })
                                                )
                                              );
                                            }

                                            return Promise.resolve();
                                          }
                                        }
                                      ]
                                    : [])
                                ]}
                              >
                                <RightComponent
                                  placeholder={t('fields.right_placeholder')}
                                  {...(isNumericProperty ? { type: 'number', min: 0, step: 'any' } : {})}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        {/* remove condition */}
                        {
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        }

                        {/* add next condition */}
                        {index === fields.length - 1 && (
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() =>
                              add({
                                left: 'watchTime',
                                operator: '>=',
                                right: '5'
                              })
                            }
                          />
                        )}

                        {/* 🟣 Ошибка на всю ширину строки для right (через shouldUpdate) */}
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldError }) => {
                            const errors = getFieldError(['conditions', field.name, 'right']);

                            if (!errors?.length) return null;

                            return (
                              <>
                                <Form.ErrorList
                                  className="commands-modal__conditions-error"
                                  errors={errors}
                                />
                              </>
                            );
                          }}
                        </Form.Item>
                      </div>
                    );
                  })}

                  {fields.length === 0 && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          left: 'watchTime',
                          operator: '>=',
                          right: '5'
                        })
                      }
                    >
                      {t('actions.add_first_if')}
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            <Form.Item
              name="ifMessage"
              rules={[{ required: true, message: t('errors.if_text_required') }]}
            >
              <TextArea rows={3} placeholder={t('fields.if_text_placeholder')} />
            </Form.Item>

            <Text strong style={{ display: 'block', marginTop: 12 }}>
              {t('sections.claimed_block')}
            </Text>

            <Form.Item
              name="claimedMessage"
              rules={[{ required: true, message: t('errors.claimed_required') }]}
            >
              <TextArea rows={3} placeholder={t('fields.claimed_placeholder')} />
            </Form.Item>

            <Text strong style={{ display: 'block', marginTop: 12 }}>
              {t('sections.else_block')}
            </Text>

            <Form.Item
              name="elseMessage"
              rules={[{ required: true, message: t('errors.else_required') }]}
            >
              <TextArea rows={3} placeholder={t('fields.else_placeholder')} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="active"
          label={t('fields.is_active_label')}
          valuePropName="checked"
          tooltip={t('fields.is_active_help')}
        >
          <Switch />
        </Form.Item>

        <div className="commands-modal__footer">
          <Button onClick={onCancel}>{t('actions.cancel')}</Button>
          <Button type="primary" htmlType="submit">
            {isEditMode ? t('actions.save') : t('actions.create')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
